import type { MinecraftModel } from "@/types"

export function normalizeModelIdForUploadedLookup(id: string): string {
  let normalized = id
  if (normalized.startsWith("#")) normalized = normalized.substring(1)
  const colonIndex = normalized.lastIndexOf(":")
  if (colonIndex !== -1) {
    normalized = normalized.substring(colonIndex + 1)
  }
  const slashIndex = normalized.lastIndexOf("/")
  if (slashIndex !== -1) {
    normalized = normalized.substring(slashIndex + 1)
  }
  return normalized
}

export function prepareIdForPath(id: string): string {
  let pathId = id

  pathId = pathId.replace(/.*:/, "")

  if (pathId.startsWith("#")) {
    pathId = pathId.substring(1)
    if (!pathId.includes("/")) {
      pathId = `block/${pathId}`
    }
  } else if (!pathId.includes("/")) {
    pathId = `item/${pathId}`
  }

  return pathId
}

export async function resolveModelInheritance(
  modelId: string,
  allUploadedModels: Map<string, MinecraftModel>,
  vanillaModelsCache: Map<string, MinecraftModel | "not_found" | "fetching">,
  maxDepth = 10,
  currentDepth = 0,
  visited: Set<string> = new Set(),
): Promise<MinecraftModel | null> {
  if (currentDepth > maxDepth) {
    console.error(`Max recursion depth reached resolving inheritance for: ${modelId}`)
    return null
  }
  if (visited.has(modelId)) {
    console.error(`Circular dependency detected for: ${modelId}`)
    return null
  }
  visited.add(modelId)

  const normalizedForUploaded = normalizeModelIdForUploadedLookup(modelId)
  const preparedPathId = prepareIdForPath(modelId)

  let model: MinecraftModel | null = null
  if (allUploadedModels.has(normalizedForUploaded)) {
    model = { ...allUploadedModels.get(normalizedForUploaded)! }
  } else {
    if (vanillaModelsCache.has(preparedPathId)) {
      const cached = vanillaModelsCache.get(preparedPathId)
      if (cached === "not_found" || cached === "fetching") return null
      model = { ...cached! }
    } else {
      vanillaModelsCache.set(preparedPathId, "fetching")
      try {
        const response = await fetch(`/models/${preparedPathId}.json`)
        if (!response.ok) {
          vanillaModelsCache.set(preparedPathId, "not_found")
          return null
        }
        const vanillaJson = await response.json()
        model = vanillaJson as MinecraftModel
        vanillaModelsCache.set(preparedPathId, { ...model })
      } catch (err) {
        console.error(`Error fetching or parsing vanilla model ${modelId}:`, err)
        vanillaModelsCache.set(preparedPathId, "not_found")
        return null
      }
    }
  }

  if (!model) {
    return null
  }

  let effectiveModel: MinecraftModel = model

  if (model.parent) {
    const parentContribution = await resolveModelInheritance(
      model.parent,
      allUploadedModels,
      vanillaModelsCache,
      maxDepth,
      currentDepth + 1,
      new Set(visited),
    )

    if (parentContribution) {
      effectiveModel = {
        ...effectiveModel,
        ...parentContribution,
        elements:
          effectiveModel.elements && effectiveModel.elements.length > 0
            ? effectiveModel.elements
            : parentContribution.elements,
        textures: { ...(parentContribution.textures || {}), ...(effectiveModel.textures || {}) },
        display: { ...(parentContribution.display || {}), ...(effectiveModel.display || {}) },
      }
      if (effectiveModel.ambientocclusion === undefined && parentContribution.ambientocclusion !== undefined) {
        effectiveModel.ambientocclusion = parentContribution.ambientocclusion
      }
    }
  }

  if (!effectiveModel.elements) {
    effectiveModel.elements = []
  }

  return effectiveModel
}
